/**
 * IDMS SQL Generation Executor
 *
 * Handles the 'idms-sql-generation' and 'pipeline-idms-sql-generation' node execution.
 * Calls the ModernizeIT API Layer /idmssqlgeneration endpoint.
 *
 * API Flow (WORKER MODE):
 * 1. Verify upstream idms_analysis AND idms_data_architecture jobs are 'completed'
 * 2. POST /idmssqlgeneration -> returns job_id with status QUEUED
 * 3. Poll GET /jobs/{job_id} -> wait until status = 'completed'
 * 4. Extract results from job.output_data
 *
 * CRITICAL: Must wait for BOTH upstream IDMS Analysis and Data Architecture to complete.
 */

import { pollJobUntilComplete } from '../../utils/jobPoller.js'
import { getAuthHeaders, handleApiError } from './authHelper.js'

/**
 * Execute the IDMS SQL generation node
 *
 * @param {Object} config - Node configuration
 * @param {Object} context - Workflow execution context
 * @param {Function} onProgress - Progress callback (progress: 0-100, message: string)
 * @param {AbortSignal} signal - Abort signal for cancellation
 * @returns {Promise<Object>} - Execution result with job info and artifacts
 */
export async function execute(config, context, onProgress = () => {}, signal = null) {
  const apiBaseUrl = context.api_base_url

  onProgress(0, 'Preparing IDMS SQL generation...')

  if (signal?.aborted) {
    throw new Error('Execution cancelled')
  }

  // 1. Verify upstream IDMS Analysis job exists
  const analysisJobId = context.jobs?.idms_analysis_job_id
  if (!analysisJobId) {
    throw new Error('SQL Generation requires IDMS Analysis job ID. Run IDMS Analysis first.')
  }

  // 2. Verify upstream IDMS Data Architecture job exists
  const dataArchJobId = context.jobs?.idms_data_architecture_job_id
  if (!dataArchJobId) {
    throw new Error('SQL Generation requires IDMS Data Architecture job ID. Run IDMS Data Architecture first.')
  }

  // 3. Wait for upstream Analysis job to complete
  console.log(`[IdmsSqlGenerationExecutor] Verifying upstream analysis job ${analysisJobId}...`)
  onProgress(2, 'Waiting for IDMS Analysis to complete...')

  try {
    await pollJobUntilComplete(analysisJobId, apiBaseUrl, {
      maxWaitSeconds: 600,
      pollInterval: 1000,
      onProgress: (progress, message) => {
        const mappedProgress = 2 + (progress * 0.03)
        onProgress(mappedProgress, `Waiting for IDMS Analysis: ${message}`)
      },
      signal
    })
    console.log(`[IdmsSqlGenerationExecutor] Upstream analysis job ${analysisJobId} completed`)
  } catch (error) {
    throw new Error(`Upstream IDMS Analysis job failed: ${error.message}`)
  }

  // 4. Wait for upstream Data Architecture job to complete
  console.log(`[IdmsSqlGenerationExecutor] Verifying upstream data architecture job ${dataArchJobId}...`)
  onProgress(5, 'Waiting for IDMS Data Architecture to complete...')

  try {
    await pollJobUntilComplete(dataArchJobId, apiBaseUrl, {
      maxWaitSeconds: 600,
      pollInterval: 1000,
      onProgress: (progress, message) => {
        const mappedProgress = 5 + (progress * 0.03)
        onProgress(mappedProgress, `Waiting for Data Architecture: ${message}`)
      },
      signal
    })
    console.log(`[IdmsSqlGenerationExecutor] Upstream data architecture job ${dataArchJobId} completed`)
  } catch (error) {
    throw new Error(`Upstream IDMS Data Architecture job failed: ${error.message}`)
  }

  onProgress(8, 'Upstream jobs complete, starting SQL generation...')

  // 5. Build request body
  const requestBody = {
    scout_account_id: context.scout_account_id,
    application_name: context.application_name,
    user_id: context.user_id,
    idms_analysis_job_id: analysisJobId,
    idms_data_architecture_job_id: dataArchJobId,
    dialect: config?.dialect || 'SQL Server',
  }

  onProgress(10, 'Submitting SQL generation job...')

  // 6. Submit the job
  let jobResponse
  try {
    const response = await fetch(`${apiBaseUrl}/idmssqlgeneration`, {
      method: 'POST',
      headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(requestBody),
      signal,
    })

    if (!response.ok) {
      await handleApiError(response)
    }

    jobResponse = await response.json()

    if (jobResponse.success === false) {
      throw new Error(jobResponse.error || 'IDMS SQL generation failed')
    }

  } catch (error) {
    if (error.name === 'AbortError') {
      throw new Error('Execution cancelled')
    }
    if (error.message.includes('Failed to fetch')) {
      throw new Error(`Cannot connect to API at ${apiBaseUrl}. Is the server running?`)
    }
    throw error
  }

  const jobId = jobResponse.job_id
  if (!jobId) {
    throw new Error('API did not return a job_id')
  }

  console.log(`[IdmsSqlGenerationExecutor] Job created: ${jobId}, polling...`)
  onProgress(15, `Job started: ${jobId}`)

  // 7. Poll for completion
  const completedJob = await pollJobUntilComplete(jobId, apiBaseUrl, {
    maxWaitSeconds: 600,
    pollInterval: 1000,
    onProgress: (progress, message) => {
      const mappedProgress = 15 + (progress * 0.80)
      onProgress(mappedProgress, message)
    },
    signal
  })

  console.log('[IdmsSqlGenerationExecutor] Job completed')
  onProgress(95, 'Extracting results...')

  const outputData = completedJob.output_data
  if (!outputData) {
    throw new Error('Job completed but has no output_data')
  }

  onProgress(100, 'IDMS SQL generation complete')

  return formatResult(jobId, outputData, context.jobs)
}

function formatResult(jobId, data, contextJobs = {}) {
  const resultJobs = {
    ...contextJobs,
    idms_sql_generation_job_id: jobId,
  }

  return {
    jobs: resultJobs,
    success: data.success !== false,
    status: data.status || 'completed',
    summary: data.summary || {},
    graph: data.graph || null,
    duration_ms: data.duration_ms,
    sql_scripts: data.sql_scripts || [],
    error: data.error || null,
  }
}

export default {
  execute,
}
